import React, {useState} from 'react';
import styled from 'styled-components';
import {motion} from 'framer-motion';
import {Link} from 'react-router-dom';
import ImageSlideshow from "./ImageSlideShow";

const Card = styled(motion.div)`
    background: white;
    border-radius: 10px;
    overflow: hidden;
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.05);
    height: 100%;
    display: flex;
    flex-direction: column;
`;

const EventImage = styled.div`
    height: 200px;
    background-size: cover;
    background-position: center;
    position: relative;

    .event-date {
        position: absolute;
        bottom: 0;
        left: 0;
        background: rgba(108, 92, 231, 0.9);
        color: white;
        padding: 0.5rem 1rem;
        font-weight: 500;
    }
`;

const EventContent = styled.div`
    padding: 1.5rem;
    flex: 1;
    display: flex;
    flex-direction: column;
`;

const EventTitle = styled.h3`
    margin-bottom: 1rem;
    color: #333;
    font-size: 1.5rem;
`;

const EventDescription = styled.p`
    color: #666;
    line-height: 1.6;
    margin-bottom: 1.5rem;
    flex: 1;
    text-align: center;
    white-space: pre-line;

    strong {
        font-weight: 700;
        color: #333;
    }
`;

const EventDetails = styled.div`
    display: flex;
    justify-content: space-between;
    color: #888;
    font-size: 0.9rem;
    margin-bottom: 1.5rem;

    .location {
        display: flex;
        align-items: center;

        svg {
            margin-right: 0.5rem;
            color: #6c5ce7;
        }
    }
`;

const EventButton = styled(Link)`
    display: inline-block;
    background-color: #6c5ce7;
    color: white;
    text-decoration: none;
    padding: 0.75rem 1.5rem;
    border-radius: 5px;
    font-weight: 500;
    text-align: center;
    transition: background-color 0.3s ease;

    &:hover {
        background-color: #5a4ad1;
    }

    ${props => props.$disabled && `
    opacity: 0.6;
    pointer-events: none;
    cursor: not-allowed;
  `}
`;

const ArticleModal = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.8);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
    padding: 2rem;
`;

const ArticleModalContent = styled(motion.div)`
    background: white;
    border-radius: 10px;
    max-width: 800px;
    width: 100%;
    max-height: 90vh;
    overflow-y: auto;
    padding: 2rem;
    position: relative;

    .close-button {
        position: absolute;
        top: 1rem;
        right: 1rem;
        background: none;
        border: none;
        font-size: 1.5rem;
        cursor: pointer;
    }

    h2 {
        margin-bottom: 1.5rem;
        color: #333;
    }

    .recipe-section {
        margin-bottom: 2rem;

        h3 {
            color: #6c5ce7;
            margin-bottom: 1rem;
            margin-top: 1rem;
        }

        p {
            margin-top: 1rem;
        }

        ul, ol {
            padding-left: 1.5rem;

            li {
                margin-bottom: 0.5rem;
            }
        }
    }
`;

const EventCard = ({event, isPast = false}) => {
    const [selectedArticle, setSelectedArticle] = useState(null);
    const sampleImages = [
        'https://dx8i2mtk03zrn.cloudfront.net/content/1000378173.jpg',
        'https://dx8i2mtk03zrn.cloudfront.net/content/botswana.jpg',
        'https://dx8i2mtk03zrn.cloudfront.net/content/1000376535.jpg',
        'https://dx8i2mtk03zrn.cloudfront.net/content/1000378356.jpg',
        'https://dx8i2mtk03zrn.cloudfront.net/content/1000348885.jpg',
    ];
    const [isModalOpen, setIsModalOpen] = useState(true);
    const [initialImageIndex, setInitialImageIndex] = useState(0);
    const openModal = (index) => {
        setInitialImageIndex(index);
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
    };

    const formatText = (text) => {
        const parts = text.split(/(\*\*.*?\*\*)/g); // Split by **bold** patterns

        return parts.map((part, i) => {
            if (part.startsWith('**') && part.endsWith('**')) {
                return <strong key={i}>{part.slice(2, -2)}</strong>;
            }
            return part;
        });
    };
    return (
        <div>
            <Card
                whileHover={{y: -10}}
                initial={{opacity: 0, y: 20}}
                whileInView={{opacity: 1, y: 0}}
                viewport={{once: true}}
                transition={{duration: 0.5}}
            >
                <EventImage style={{backgroundImage: `url(${event.image})`}}>
                    <div className="event-date">{event.date}</div>
                </EventImage>
                <EventContent>
                    <EventTitle>{event.title}</EventTitle>
                    <EventDescription>{formatText(event.description)}</EventDescription>
                    <EventDetails>
                        <div className="location">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                                 xmlns="http://www.w3.org/2000/svg">
                                <path
                                    d="M12 2C8.13 2 5 5.13 5 9C5 14.25 12 22 12 22C12 22 19 14.25 19 9C19 5.13 15.87 2 12 2ZM12 11.5C10.62 11.5 9.5 10.38 9.5 9C9.5 7.62 10.62 6.5 12 6.5C13.38 6.5 14.5 7.62 14.5 9C14.5 10.38 13.38 11.5 12 11.5Z"
                                    fill="currentColor"/>
                            </svg>
                            {event.location}
                        </div>
                    </EventDetails>
                    <EventButton $disabled={event.sections === undefined} to="/events"
                                 onClick={() => setSelectedArticle(event)}>
                        Read More
                    </EventButton>
                </EventContent>
            </Card>
            {selectedArticle && (
                <ArticleModal onClick={() => setSelectedArticle(null)}>
                    <ArticleModalContent
                        onClick={e => e.stopPropagation()}
                        initial={{opacity: 0, y: 50}}
                        animate={{opacity: 1, y: 0}}
                    >
                        <button className="close-button" onClick={() => setSelectedArticle(null)}>✕</button>
                        <h2>{selectedArticle.title}</h2>
                        <p>Reported By: <i>{selectedArticle.author}</i></p>
                        <ImageSlideshow
                            images={selectedArticle.slideshow}
                            startIndex={initialImageIndex}
                            onClose={closeModal}
                        />
                        {selectedArticle.sections.map((section, index) => (
                            <div className="recipe-section">
                                <p>{section.content}</p>
                                <h3>{section.title}</h3>
                                <ul>
                                    {section.sentences.map((sentence, index) => (
                                        <li key={sentence.id}>{sentence.text}</li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </ArticleModalContent>
                </ArticleModal>
            )}
        </div>
    );
};

export default EventCard;